
import { useState, useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Loader2, CheckCircle, XCircle } from "lucide-react";

const ConfirmInvitation = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [isLoading, setIsLoading] = useState(true);
  const [isConfirming, setIsConfirming] = useState(false);
  const [confirmationUrl, setConfirmationUrl] = useState<string | null>(null);

  useEffect(() => {
    // Get the confirmation URL from query parameters
    const confirmationUrlParam = searchParams.get('confirmation_url');
    
    if (!confirmationUrlParam) {
      toast({
        title: "Ogiltig länk",
        description: "Bekräftelselänken är ogiltig.",
        variant: "destructive",
      });
      navigate('/admin');
      return;
    }

    setConfirmationUrl(confirmationUrlParam);
    setIsLoading(false);
  }, [searchParams, navigate, toast]);

  const handleConfirmation = async () => {
    if (!confirmationUrl) return;

    setIsConfirming(true);
    try {
      // Navigate to the actual Supabase confirmation URL
      window.location.href = confirmationUrl;
    } catch (error: any) {
      console.error('Error confirming invitation:', error);
      toast({
        title: "Ett fel uppstod",
        description: "Kunde inte bekräfta inbjudan. Försök igen.",
        variant: "destructive",
      });
      setIsConfirming(false);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4 bg-background">
        <Card className="w-full max-w-md p-6 space-y-6 text-center">
          <Loader2 className="h-8 w-8 animate-spin mx-auto" />
          <p>Laddar bekräftelse...</p>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-4 bg-background">
      <Card className="w-full max-w-md p-6 space-y-6 text-center">
        <CheckCircle className="h-12 w-12 text-green-500 mx-auto" />
        <div className="space-y-2">
          <h1 className="text-2xl font-semibold tracking-tight">
            Bekräfta din inbjudan
          </h1>
          <p className="text-sm text-muted-foreground">
            Hej! Du har blivit inbjuden som gruppledare. Klicka på knappen nedan för att slutföra din registrering och bekräfta ditt konto.
          </p>
        </div>

        <Button 
          onClick={handleConfirmation}
          disabled={isConfirming}
          className="w-full"
        >
          {isConfirming ? (
            <div className="flex items-center gap-2">
              <Loader2 className="h-4 w-4 animate-spin" />
              <span>Bekräftar...</span>
            </div>
          ) : (
            "Bekräfta inbjudan"
          )}
        </Button>

        <Button
          variant="ghost"
          className="w-full"
          onClick={() => navigate('/admin')}
        >
          Avbryt
        </Button>
      </Card>
    </div>
  );
};

export default ConfirmInvitation;
